import { useRef, useEffect, useState, forwardRef } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ContainerTextFlip } from "@/components/ui/container-text-flip";
import { Container, SectionSubheading } from "./shared";
import { cn } from "@/lib/utils";

// ----------------------------------------------------------------------
// Constants
// ----------------------------------------------------------------------

const DASHBOARD_IMAGE_DARK = "https://assets.uselint.com/core/dashboard-preview.webp";
const DASHBOARD_IMAGE_LIGHT = "https://assets.uselint.com/core/dashboard-preview-light.webp";

// ----------------------------------------------------------------------
// Background Grid Component
// ----------------------------------------------------------------------

function BackgroundGrid() {
  return (
    <div className="pointer-events-none absolute inset-0 z-0 grid h-full w-full -rotate-45 transform select-none overflow-hidden opacity-45 dark:opacity-25">
      <div className="grid h-[200%] w-[200%] grid-cols-6 gap-8 -translate-x-1/4 -translate-y-1/4">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="relative h-full w-full">
            <GridLineVertical className="left-0" />
            {i === 2 && (
              <div className="absolute inset-0 bg-gradient-to-b from-transparent via-emerald-500/5 to-transparent dark:via-emerald-500/10" />
            )}
            {i === 3 && (
              <div className="absolute inset-0 bg-gradient-to-b from-transparent via-teal-500/5 to-transparent dark:via-teal-500/10" />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function GridLineVertical({ className }: { className?: string }) {
  return (
    <div
      className={cn(
        "absolute top-0 h-full w-px",
        "bg-gradient-to-b from-transparent via-neutral-300 to-transparent",
        "dark:via-neutral-700",
        "[mask-image:linear-gradient(to_bottom,transparent,black_20%,black_80%,transparent)]",
        className
      )}
    />
  );
}

// ----------------------------------------------------------------------
// Animated Beam Component
// ----------------------------------------------------------------------

type BeamOptions = {
  initialX?: number;
  translateX?: number;
  initialY?: number;
  translateY?: number;
  rotate?: number;
  duration?: number;
  delay?: number;
  repeatDelay?: number;
  className?: string;
};

type CollisionState = {
  detected: boolean;
  coordinates: { x: number; y: number } | null;
};

const AnimatedBeam = forwardRef<
  HTMLDivElement,
  {
    containerRef: React.RefObject<HTMLDivElement | null>;
    parentRef: React.RefObject<HTMLDivElement | null>;
    beamOptions?: BeamOptions;
  }
>(({ parentRef, containerRef, beamOptions = {} }, _ref) => {
  const beamRef = useRef<HTMLDivElement>(null);
  const [collision, setCollision] = useState<CollisionState>({
    detected: false,
    coordinates: null,
  });
  const [beamKey, setBeamKey] = useState(0);
  const [cycleCollisionDetected, setCycleCollisionDetected] = useState(false);

  useEffect(() => {
    const checkCollision = () => {
      if (
        beamRef.current &&
        containerRef.current &&
        parentRef.current &&
        !cycleCollisionDetected
      ) {
        const beamRect = beamRef.current.getBoundingClientRect();
        const containerRect = containerRef.current.getBoundingClientRect();
        const parentRect = parentRef.current.getBoundingClientRect();

        if (beamRect.bottom >= containerRect.top && beamRect.top <= containerRect.bottom) {
          const relativeX = beamRect.left - parentRect.left + beamRect.width / 2;
          const relativeY = beamRect.bottom - parentRect.top;

          setCollision({
            detected: true,
            coordinates: { x: relativeX, y: relativeY },
          });
          setCycleCollisionDetected(true);
          if (beamRef.current) {
            beamRef.current.style.opacity = "0";
          }
        }
      }
    };

    const animationInterval = setInterval(checkCollision, 50);
    return () => clearInterval(animationInterval);
  }, [cycleCollisionDetected, containerRef, parentRef]);

  useEffect(() => {
    if (collision.detected && collision.coordinates) {
      setTimeout(() => {
        setCollision({ detected: false, coordinates: null });
        setCycleCollisionDetected(false);
        if (beamRef.current) {
          beamRef.current.style.opacity = "1";
        }
      }, 2000);

      setTimeout(() => {
        setBeamKey((prevKey) => prevKey + 1);
      }, 2000);
    }
  }, [collision]);

  return (
    <>
      <motion.div
        key={beamKey}
        ref={beamRef}
        initial={{
          translateY: beamOptions.initialY ?? -200,
          translateX: beamOptions.initialX ?? 0,
          rotate: beamOptions.rotate ?? -45,
        }}
        animate={{
          translateY: beamOptions.translateY ?? 1000,
          translateX: beamOptions.translateX ?? 700,
          rotate: beamOptions.rotate ?? -45,
        }}
        transition={{
          duration: beamOptions.duration ?? 8,
          repeat: Infinity,
          repeatType: "loop",
          ease: "linear",
          delay: beamOptions.delay ?? 0,
          repeatDelay: beamOptions.repeatDelay ?? 0,
        }}
        className={cn(
          "absolute left-1/4 top-0 z-10 m-auto h-14 w-px rounded-full opacity-60 dark:opacity-40",
          "bg-gradient-to-t from-emerald-500 via-teal-400 to-transparent",
          beamOptions.className
        )}
      />
      <AnimatePresence>
        {collision.detected && collision.coordinates && (
          <Explosion
            key={`${collision.coordinates.x}-${collision.coordinates.y}`}
            style={{
              left: `${collision.coordinates.x}px`,
              top: `${collision.coordinates.y}px`,
              transform: "translate(-50%, -50%)",
            }}
          />
        )}
      </AnimatePresence>
    </>
  );
});

AnimatedBeam.displayName = "AnimatedBeam";

// ----------------------------------------------------------------------
// Explosion Component
// ----------------------------------------------------------------------

function Explosion({ style, className }: { style?: React.CSSProperties; className?: string }) {
  const particles = Array.from({ length: 12 }, (_, index) => ({
    id: index,
    directionX: Math.floor(Math.random() * 100 - 50),
    directionY: Math.floor(Math.random() * -60 - 10),
  }));

  return (
    <div style={style} className={cn("absolute z-50 h-2 w-2", className)}>
      {/* Central flash */}
      <motion.div
        initial={{ opacity: 0, scale: 0.5 }}
        animate={{ opacity: [0, 1, 0], scale: [0.5, 1.5, 0.5] }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        className="absolute -inset-4 rounded-full bg-emerald-500/20 blur-md"
      />
      {/* Horizontal flash line */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: [0, 1, 0] }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.8, ease: "easeOut" }}
        className="absolute -inset-x-6 top-0 m-auto h-[2px] w-12 rounded-full bg-gradient-to-r from-transparent via-emerald-400/80 to-transparent blur-sm"
      />
      {/* Particles */}
      {particles.map((particle) => (
        <motion.span
          key={particle.id}
          initial={{ x: 0, y: 0, opacity: 1 }}
          animate={{
            x: particle.directionX,
            y: particle.directionY,
            opacity: 0,
          }}
          transition={{
            duration: Math.random() * 1.2 + 0.4,
            ease: "easeOut",
          }}
          className={cn(
            "absolute h-1 w-1 rounded-full",
            particle.id % 2 === 0
              ? "bg-gradient-to-b from-emerald-400 to-emerald-600"
              : "bg-gradient-to-b from-teal-400 to-teal-600"
          )}
        />
      ))}
    </div>
  );
}

// ----------------------------------------------------------------------
// Main Hero Section Component
// ----------------------------------------------------------------------

export function HeroSection() {
  const parentRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  return (
    <section
      ref={parentRef}
      className="relative min-h-screen pt-16 pb-8 md:pt-28 md:pb-16 lg:pt-36 lg:pb-20 overflow-hidden"
    >
      {/* Background gradient */}
      <div className="absolute inset-0 bg-gradient-to-b from-background via-background to-neutral-50 dark:to-neutral-950/50" />

      {/* Animated grid background */}
      <BackgroundGrid />

      {/* Animated beams */}
      <AnimatedBeam
        parentRef={parentRef}
        containerRef={containerRef}
        beamOptions={{
          initialX: -400,
          translateX: 600,
          duration: 7,
          repeatDelay: 3,
        }}
      />
      <AnimatedBeam
        parentRef={parentRef}
        containerRef={containerRef}
        beamOptions={{
          initialX: -200,
          translateX: 900,
          duration: 5,
          repeatDelay: 4,
          delay: 1,
        }}
      />
      <AnimatedBeam
        parentRef={parentRef}
        containerRef={containerRef}
        beamOptions={{
          initialX: 100,
          translateX: 1100,
          duration: 6,
          repeatDelay: 2,
          delay: 2,
        }}
      />
      <AnimatedBeam
        parentRef={parentRef}
        containerRef={containerRef}
        beamOptions={{
          initialX: 300,
          translateX: 1300,
          duration: 8,
          repeatDelay: 5,
          delay: 0.5,
        }}
      />

      <Container className="relative z-20">
        <div className="text-center max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="flex justify-center mb-4"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-border bg-background/80 backdrop-blur-sm text-sm font-medium text-foreground shadow-sm">
              <CheckCircle2 className="w-4 h-4 text-emerald-500" />
              <span>5% platform fee • Weekly payouts</span>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <h1 className="mb-6 max-w-5xl mx-auto text-center text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-semibold tracking-tight text-foreground">
              <span className="flex items-baseline justify-center gap-2 sm:gap-3">
                <span>Sell your</span>
                <ContainerTextFlip
                  words={["Products", "Courses", "Downloads", "Coaching", "Templates", "Ebooks"]}
                  interval={3500}
                  animationDuration={550}
                  className={cn(
                    "!text-2xl sm:!text-3xl md:!text-4xl lg:!text-5xl xl:!text-6xl !font-semibold !pt-1 !pb-2 !min-w-[140px] sm:!min-w-[180px] md:!min-w-[220px] lg:!min-w-[280px] xl:!min-w-[320px]",
                    "!shadow-none dark:!shadow-none !ring-1 !ring-border/50 backdrop-blur-sm",
                    "![background:rgba(245,245,245,0.6)] dark:![background:rgba(23,23,23,0.45)]"
                  )}
                />
              </span>
              <span className="block mt-3 font-medium">Keep more. Get paid faster.</span>
            </h1>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <SectionSubheading className="mx-auto mb-8">
              Transparent fees. Fast payouts. Creator-first tools.
            </SectionSubheading>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="flex items-center justify-center gap-4 flex-col sm:flex-row mb-6"
          >
            <Button
              size="lg"
              className="shadow-lg shadow-emerald-500/20 w-full sm:w-auto bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600"
              asChild
            >
              <Link to="/auth/signup">Start Creating</Link>
            </Button>
            <Button size="lg" variant="outline" className="w-full sm:w-auto backdrop-blur-sm" asChild>
              <Link to="#features">
                See the Platform
              </Link>
            </Button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="flex flex-wrap items-center justify-center gap-x-3 gap-y-2 text-sm text-muted-foreground"
          >
            <span>Free to start • No monthly fee</span>
            <span className="text-border">•</span>
            <span>Stripe Connect</span>
          </motion.div>
        </div>

        {/* Dashboard Preview Container - collision target */}
        <motion.div
          ref={containerRef}
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: "easeOut", delay: 0.5 }}
          className="relative mt-12 md:mt-16 lg:mt-20 mx-auto max-w-6xl"
        >
          <div className="relative rounded-[32px] border border-neutral-200/50 dark:border-neutral-700/50 bg-neutral-100/80 dark:bg-neutral-800/50 p-2 md:p-4">
            <div className="rounded-[24px] border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-black overflow-hidden">
              <img
                src={DASHBOARD_IMAGE_LIGHT}
                alt="Lint Dashboard - Track your earnings, students, and courses"
                className="w-full h-auto block dark:hidden rounded-[20px]"
                fetchPriority="high"
                decoding="async"
              />
              <img
                src={DASHBOARD_IMAGE_DARK}
                alt="Lint Dashboard - Track your earnings, students, and courses"
                className="w-full h-auto hidden dark:block rounded-[20px]"
                fetchPriority="high"
                decoding="async"
              />
            </div>
          </div>
        </motion.div>
      </Container>

      {/* Bottom fade gradient */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-neutral-100 dark:from-neutral-900 to-transparent z-10" />
    </section>
  );
}
